import type { ReactNode } from 'react';
import { site } from '@/content/site';

/** Navigates directly to Cal.com in the current tab, without an embed or popup. */

interface BookCallButtonProps {
  children?: ReactNode;
  className?: string;
  /** Matches Button's variants so this drops in beside them. */
  variant?: 'primary' | 'brand' | 'secondary' | 'inverse' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
}

export function BookCallButton({
  children,
  className = '',
  variant = 'secondary',
  size = 'md',
}: BookCallButtonProps) {
  // Mirrors Button so the two are visually interchangeable.
  const base =
    'group inline-flex items-center justify-center gap-2 rounded-full font-medium transition-all duration-500 ease-brand';
  const variants = {
    primary: 'bg-accent text-ink hover:bg-accent-deep',
    brand: 'bg-brand text-white hover:bg-brand-deep',
    secondary: 'border border-ink/20 text-ink hover:border-ink hover:bg-ink hover:text-white',
    inverse: 'bg-white text-ink hover:bg-accent hover:text-ink',
    ghost: 'text-current hover:opacity-70',
  } as const;
  const sizes = {
    sm: 'min-h-11 px-5 text-sm',
    md: 'min-h-12 px-7 text-base',
    lg: 'min-h-14 px-9 text-base',
  } as const;

  return (
    <a
      href={`https://cal.com/${site.booking.link}`}
      className={`${base} ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {children ?? site.booking.label}
      <svg
        viewBox="0 0 24 24"
        className="h-4 w-4 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
        <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
      </svg>
    </a>
  );
}
